import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useTranslation } from "react-i18next"
import { IndexProps, useIndexProps } from "./page-props"
import { PageBody } from "@/components/ui/layout"
import { Ban, Eye, Inbox, ListTree, PlusCircle } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyHeader,
  EmptyContent,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Badge } from "@/components/ui/badge"
import { ListingCard } from "@/components/shared/listing-card"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Link } from "@/lib/page-rendering"

export default function Index() {
  const { t } = useTranslation("educator_relief_listings", {
    keyPrefix: "index",
  })
  const { listings } = useIndexProps()

  return (
    <>
      <EducatorPageChrome
        action={
          <ActionLink variant="solid" href={"./listings/new"} Icon={PlusCircle}>
            {t("create")}
          </ActionLink>
        }
      />
      <PageBody>
        <ul className="flex flex-col gap-6">
          {listings.length === 0 ? (
            <Card>
              <CardContent>
                <Empty data-testid="relief-listings-empty">
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                    <EmptyDescription>
                      {t("empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button asChild>
                      <Link href={"./listings/new"}>{t("create")}</Link>
                    </Button>
                  </EmptyContent>
                </Empty>
              </CardContent>
            </Card>
          ) : (
            listings.map(({ listing_profile, collection }) => (
              <li key={listing_profile.id} className="contents">
                <ListingCard listing={listing_profile}>
                  {collection.map((relief_listing) => (
                    <ReliefListingCard
                      key={relief_listing.id}
                      relief_listing={relief_listing}
                    />
                  ))}
                </ListingCard>
              </li>
            ))
          )}
        </ul>
      </PageBody>
    </>
  )
}

function ReliefListingCard({
  relief_listing,
}: {
  relief_listing: IndexProps["listings"][number]["collection"][number]
}) {
  const { t } = useTranslation("educator_relief_listings", {
    keyPrefix: "index",
  })
  const { t: tListingState } = useTranslation("educator_relief_listings", {
    keyPrefix: "state",
  })
  return (
    <Card data-testid="relief-listing-card" role="listitem">
      <CardHeader>
        <CardTitle>{relief_listing.title}</CardTitle>
        <Badge data-testid="listing-state">
          {tListingState(relief_listing.state)}
        </Badge>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-base">
          <dl className="grid w-fit grid-cols-2 gap-2 [&_dd]:font-medium">
            <dt>{t("start_date")}</dt>
            <dd data-testid="start_date">{relief_listing.start_date}</dd>
            <dt>{t("end_date")}</dt>
            <dd data-testid="end_date">{relief_listing.end_date}</dd>
          </dl>
        </CardDescription>
      </CardContent>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        <Button asChild>
          <Link href={`./listings/${relief_listing.id}/requests`}>
            <ListTree />
            {t("actions.requests")}
          </Link>
        </Button>
        <Button asChild>
          <Link href={`/search/relief_listings/${relief_listing.id}`}>
            <Eye />
            {t("actions.view_listing")}
          </Link>
        </Button>
        {relief_listing.state === "open" && (
          <ConfirmationDialog
            description={t("actions.close_description")}
            action={
              <Link
                className={buttonVariants({ variant: "destructive" })}
                method="post"
                href={`./listings/${relief_listing.id}/close`}
                as={Button}
              >
                {t("actions.close")}
              </Link>
            }
          >
            <ConfirmationDialogTrigger asChild>
              <Button variant={"destructive-outline"}>
                <Ban />
                {t("actions.close")}
              </Button>
            </ConfirmationDialogTrigger>
          </ConfirmationDialog>
        )}
      </CardFooter>
    </Card>
  )
}
